/**
 * Localization Provider for qs-status-frontend
 * Integrates i18next with shared-ui's LocalizationContext
 */

import { observer } from 'mobx-react-lite';
import {
  LocalizationProvider as SharedUILocalizationProvider,
  LocalizationContextValue,
} from '@emfit/shared-ui';
import { t, LocalizationService } from '../services/LocalizationService';
import { format } from 'date-fns';
import { UserStore } from '../stores';

interface LocalizationProviderProps {
  children: React.ReactNode;
}

export const LocalizationProvider = observer(
  ({ children }: LocalizationProviderProps) => {
    // Read from observable stores for reactivity
    const locale = LocalizationService.locale || 'en-US';
    const dateFormat =
      UserStore.userSettings?.date_format === 'DD-MM'
        ? 'dd.MM.yyyy'
        : 'MM/dd/yyyy';
    const timeFormat: '12' | '24' =
      UserStore.userSettings?.time_format === 'H12' ? '12' : '24';

    // Create localization context value
    const localizationValue: LocalizationContextValue = {
      locale,

      t: (key: string, options?: Record<string, unknown>) => {
        return t(key, options);
      },

      formatDate: (date: Date | string | number, options?) => {
        const dateObj = typeof date === 'object' ? date : new Date(date);
        const formatString = options?.format || dateFormat;
        return format(dateObj, formatString);
      },

      formatTime: (date: Date | string | number) => {
        const dateObj = typeof date === 'object' ? date : new Date(date);
        const formatString = timeFormat === '24' ? 'HH:mm' : 'h:mm a';
        return format(dateObj, formatString);
      },

      formatDateTime: (date: Date | string | number) => {
        const dateObj = typeof date === 'object' ? date : new Date(date);
        const timeFormatString = timeFormat === '24' ? 'HH:mm' : 'h:mm a';
        return format(dateObj, `${dateFormat} ${timeFormatString}`);
      },

      formatNumber: (value: number, options?: Intl.NumberFormatOptions) => {
        return new Intl.NumberFormat(locale, options).format(value);
      },

      dateFormat,
      timeFormat,
    };

    return (
      <SharedUILocalizationProvider value={localizationValue}>
        {children}
      </SharedUILocalizationProvider>
    );
  }
);

LocalizationProvider.displayName = 'LocalizationProvider';
